import React, { useEffect, useState } from 'react';
import { EventsService, EventInfo } from '../services/eventsService';
import { useTranslation } from 'react-i18next';
import { safeTranslate, useNullableTranslation } from '../utils/translation';
import { useNavigate } from 'react-router-dom';
import CustomTable from '../components/tables/customTable/CustomTable';
import { formatDateTime } from '../utils/formatUtils';

const AllEventsList: React.FC = () => {
  const { t } = useTranslation();
  const getTranslation = useNullableTranslation(t);
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get CSS variables for consistent coloring
  const getCSSVariable = (variableName: string): string => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await EventsService.getAllEvents();
        setEvents(data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div
    >
      <h2 style={{
        fontSize: 32,
        fontWeight: 700,
        marginBottom: 32,
        color: getCSSVariable('--primaryColor') || '#f59e0b',
        letterSpacing: '-1px',
      }}>{getTranslation('all-events') || 'All Events List'}</h2>
      {loading && <div style={{ padding: '2rem', textAlign: 'center' }}>{getTranslation('loading') || 'Loading...'}</div>}
      {error && <div style={{ color: 'red', padding: '1rem' }}>{error}</div>}
      {!loading && !error && (
          <CustomTable
            headData={[
              getTranslation('eventName') || 'Event Name',
              getTranslation('startTime') || 'Start Time',
              getTranslation('endTime') || 'End Time',
              getTranslation('location') || 'Location',
              getTranslation('actions') || 'Actions',
            ]}
            bodyData={events}
            renderRow={(event, index) => (
              <tr
                key={event.id}
                style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s', cursor: 'pointer', background: 'white' }}
                onMouseOver={e => (e.currentTarget.style.background = '#f9f9f9')}
                onMouseOut={e => (e.currentTarget.style.background = 'white')}
              >
                <td style={{ padding: '20px 24px', fontWeight: 600, fontSize: 18, color: '#333' }}>{event.name}</td>
                <td style={{ padding: '20px 24px', fontSize: 16, color: '#333' }}>{formatDateTime(event.startTime)}</td>
                <td style={{ padding: '20px 24px', fontSize: 16, color: '#333' }}>{formatDateTime(event.endTime)}</td>
                <td style={{ padding: '20px 24px', fontSize: 16, color: '#333' }}>{event.location}</td>
                <td style={{ padding: '20px 24px' }}>
                  <button
                    style={{
                      background: getCSSVariable('--primaryColor') || '#f59e0b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '12px 32px',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 16,
                      boxShadow: `0 1px 4px 0 ${getCSSVariable('--primaryColorRgba') || 'rgba(245, 158, 11, 0.2)'}`,
                      transition: 'background 0.2s',
                      outline: 'none',
                    }}
                    onMouseOver={e => (e.currentTarget.style.background = getCSSVariable('--primaryColorAlpha80') || 'rgba(245, 158, 11, 0.8)')}
                    onMouseOut={e => (e.currentTarget.style.background = getCSSVariable('--primaryColor') || '#f59e0b')}
                    onClick={() => navigate(`/events/${event.id}/analytics/dashboard`, { state: { event } })}
                  >
                    {getTranslation('viewDashboard') || 'View Dashboard'}
                  </button>
                </td>
              </tr>
            )}
          />
      )}
    </div>
  );
};

export default AllEventsList;
